import { Component, DestroyRef, inject, signal } from '@angular/core';
import { CraftEntityService } from '../../services/craft-entity.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CraftType,
  DynamicRouteHandlingService,
  GenericDropdownComponent,
  InputFieldComponent
} from '@zayan-erp/shared';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Validators } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { CraftEntity, CreateCraftEntity } from '../../models/craft-entity.model';
import { Observable, of, tap, map } from 'rxjs';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { DropdownData } from '@zayan-erp/shared';
import { LookupsService } from '@zayan-erp/shared';
import { MajorCodeService } from '../../services/major-code.service';
import { MinorCodesService } from '../../services/minor-codes.service';
import { JobTypeService } from '../../services/job-type.service';

@Component({
  selector: 'lib-craft-entity-form',
  imports: [InputFieldComponent, FormsModule, ReactiveFormsModule, GenericDropdownComponent, ToggleSwitchModule],
  templateUrl: './craft-entity-form.component.html'
})
export class CraftEntityFormComponent {
  #craftEntityService = inject(CraftEntityService);
  #router = inject(Router);
  #dynamicRouteHandling = inject(DynamicRouteHandlingService);
  #activatedRoute = inject(ActivatedRoute);
  #lookupsService = inject(LookupsService);
  #majorCodeService = inject(MajorCodeService);
  #minorCodesService = inject(MinorCodesService);
  #jobTypeService = inject(JobTypeService);
  #destroyRef = inject(DestroyRef);

  selectedCraftEntityId = signal<string | null>(this.#activatedRoute.snapshot.paramMap.get('id') ?? null);
  craftEntityForm = new FormGroup({
    code: new FormControl<string | null>(null, Validators.required),
    description: new FormControl<string | null>(null, Validators.required),
    type: new FormControl<string | null>(null, Validators.required),
    taskId: new FormControl<string | null>(null),
    unitOfMeasureId: new FormControl<string | null>(null),
    jobTypeId: new FormControl<string | null>(null),
    majorCodeId: new FormControl<string | null>(null),
    minorCodeId: new FormControl<string | null>(null),
    retentionPercent: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(100)
    ])
  });

  typeList = signal<DropdownData[]>(
    Object.keys(CraftType)
      .filter(key => !isNaN(Number(CraftType[key as keyof typeof CraftType])))
      .map(key => ({
        id: String(CraftType[key as keyof typeof CraftType]),
        firstLabel: key
      }))
  );
  taskList = toSignal(this.#lookupsService.readTasks() as unknown as Observable<DropdownData[]>);
  jobTypeList = toSignal(
    this.#jobTypeService
      .readJobTypes()
      .pipe(
        map(res =>
          res.map(item => ({ id: item.id, firstLabel: item.code, secondLabel: item.description }) as DropdownData)
        )
      )
  );
  jobTypeId = toSignal(this.craftEntityForm.get('jobTypeId')!.valueChanges);
  majorCodeList = rxResourceFixed({
    params: () => ({ jobTypeId: this.jobTypeId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (params.jobTypeId) {
        return this.#majorCodeService
          .readMajorCodes(params.jobTypeId)
          .pipe(
            map(res =>
              res.map(item => ({ id: item.id, firstLabel: item.code, secondLabel: item.description }) as DropdownData)
            )
          );
      }
      return of([]);
    }
  });
  majorCodeId = toSignal(this.craftEntityForm.get('majorCodeId')!.valueChanges);
  minorCodeList = rxResourceFixed({
    params: () => ({ jobTypeId: this.jobTypeId(), majorCodeId: this.majorCodeId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (params.jobTypeId && params.majorCodeId) {
        return this.#minorCodesService
          .readMinorCodes(params.majorCodeId, params.jobTypeId)
          .pipe(
            map(res =>
              res.map(item => ({ id: item.id, firstLabel: item.code, secondLabel: item.description }) as DropdownData)
            )
          );
      }
      return of([]);
    }
  });
  unitOfMeasureList = toSignal(
    this.#lookupsService
      .readUofm()
      .pipe(
        map(res =>
          res.map(item => ({ id: item.id, firstLabel: item.code, secondLabel: item.description }) as DropdownData)
        )
      ),
    { initialValue: [] as DropdownData[] }
  );

  constructor() {
    this.#dynamicRouteHandling.submitButton.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.submit());

    this.craftEntityForm
      .get('jobTypeId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.craftEntityForm.get('majorCodeId')?.setValue(null);
      });
    this.craftEntityForm
      .get('majorCodeId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.craftEntityForm.get('minorCodeId')?.setValue(null);
      });
  }

  editRxResource = rxResourceFixed({
    params: () => ({ id: this.selectedCraftEntityId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.id) {
        return this.#craftEntityService.readCraftEntityById(params.id).pipe(
          tap(craftEntity => {
            this.editForm(craftEntity);
          })
        );
      }
      return of(null);
    }
  });

  editForm(craftEntity: CraftEntity) {
    this.craftEntityForm.patchValue({
      code: craftEntity.code,
      description: craftEntity.description,
      type: craftEntity.type.toString(),
      taskId: craftEntity.taskId,
      unitOfMeasureId: craftEntity.unitOfMeasureId,
      jobTypeId: craftEntity.jobTypeId,
      majorCodeId: craftEntity.majorCodeId,
      minorCodeId: craftEntity.minorCodeId,
      retentionPercent: craftEntity.retentionPercent
    });
  }
  submit(): void {
    if (this.craftEntityForm.invalid) {
      this.craftEntityForm.markAllAsTouched();
      return;
    }
    this.craftEntityForm.value.type = Number(this.craftEntityForm.value.type) as any;
    if (this.selectedCraftEntityId()) this.updateCraft();
    else this.createCraft();
  }

  createCraft() {
    this.#craftEntityService.createCraftEntity(this.craftEntityForm.value as CreateCraftEntity).subscribe(() => {
      this.afterSubmitSuccess();
    });
  }

  updateCraft() {
    this.#craftEntityService
      .updateCraftEntity(this.craftEntityForm.value as CreateCraftEntity, this.selectedCraftEntityId()!)
      .subscribe(() => {
        this.afterSubmitSuccess();
      });
  }
  afterSubmitSuccess() {
    this.#router.navigate([this.#activatedRoute.snapshot.data['returnRoute']], { queryParamsHandling: 'preserve' });
    this.#dynamicRouteHandling.reload.next(!this.#dynamicRouteHandling.reload.value);
  }
}
